/* eslint-disable complexity */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { Abteilung } from '../../../entities';
import { faExclamationCircle } from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';
import { Markierung } from '../../Markierung';
import {
  faTimes,
  faAnglesUpDown,
  faFilter,
} from '@fortawesome/pro-regular-svg-icons';
import { useAbteilungen } from '../../../hooks/useAbteilungen';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { getColorByOption } from '@/domain/arbeitszeiten/components/Comboboxen/AbteilungsCombobox/getColorByOption';
import { AbteilungComboboxPopoverContent } from '@/domain/arbeitszeiten/components/Comboboxen/AbteilungsCombobox/AbteilungComboboxPopoverContent';
import { Button } from '@/components/ui/button';

// eslint-disable-next-line max-lines-per-function
export function AbteilungsFilter({
  onSelect,
}: {
  onSelect: (abteilungen: Abteilung[]) => void;
}) {
  const { currentStandort } = useStandortContext();
  const { abteilungen, error } = useAbteilungen(currentStandort.id);
  const { t } = useTranslation();

  const [selectedAbteilungen, setSelectedAbteilungen] = useState<Abteilung[]>(
    [],
  );

  if (error) {
    return (
      <Button
        variant="ghost"
        role="combobox"
        className="w-[240px] justify-between bg-white border border-neutrals-300">
        {selectedAbteilungen.length > 0 ? (
          <div className="flex gap-2 items-center">
            <Markierung
              color={getColorByOption(
                selectedAbteilungen[selectedAbteilungen.length - 1].id,
                abteilungen,
              )}
            />
            {selectedAbteilungen[selectedAbteilungen.length - 1].name}
          </div>
        ) : (
          t('domain.arbeitszeiten.uebersicht.abteilungfilter.placeholder')
        )}
        <FontAwesomeIcon
          icon={faExclamationCircle}
          className="text-primary-red-500"
        />
      </Button>
    );
  }

  return (
    <>
      <div className="flex lg:hidden">
        <Popover>
          <PopoverTrigger asChild>
            <div className="relative">
              {selectedAbteilungen.length > 0 && (
                <span className="absolute top-[5px] right-[5px] size-[5px] inline-block text-center font-semibold rounded-full bg-primary-blue-600" />
              )}
              <Button variant="ghost" size="icon">
                <FontAwesomeIcon icon={faFilter} />
              </Button>
            </div>
          </PopoverTrigger>
          <AbteilungComboboxPopoverContent
            abteilungen={abteilungen}
            setSelectedAbteilungen={setSelectedAbteilungen}
            selectedAbteilungen={selectedAbteilungen}
            onSelect={onSelect}
          />
        </Popover>
      </div>
      <div className="hidden lg:inline">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              role="combobox"
              className="w-[280px] h-[42px] bg-white border border-neutrals-300 flex justify-between items-center">
              {selectedAbteilungen.length > 0 ? (
                <div className="flex gap-1 items-baseline font-normal">
                  <Markierung
                    color={getColorByOption(
                      selectedAbteilungen[selectedAbteilungen.length - 1].id,
                      abteilungen,
                    )}
                  />
                  <span className="truncate w-[128px] text-start text-base ">
                    {selectedAbteilungen[selectedAbteilungen.length - 1].name}
                  </span>
                  {selectedAbteilungen.length > 1 && (
                    <span className="text-neutrals-700">
                      {'+' + (selectedAbteilungen.length - 1) + ' weitere'}
                    </span>
                  )}
                </div>
              ) : (
                <span className="text-neutrals-700 w-20 font-normal">
                  {t(
                    'domain.arbeitszeiten.uebersicht.abteilungfilter.placeholder',
                  )}
                </span>
              )}
              <div className="flex items-baseline ps-2">
                {selectedAbteilungen.length > 0 && (
                  <div
                    className="group"
                    onClick={(event) => {
                      event.preventDefault();
                      setSelectedAbteilungen([]);
                      onSelect([]);
                    }}>
                    <FontAwesomeIcon
                      className="group-hover:text-primary-blue-600 text-neutrals-700"
                      size="lg"
                      icon={faTimes}
                    />
                  </div>
                )}
                <FontAwesomeIcon icon={faAnglesUpDown} />
              </div>
            </Button>
          </PopoverTrigger>
          <AbteilungComboboxPopoverContent
            abteilungen={abteilungen}
            setSelectedAbteilungen={setSelectedAbteilungen}
            selectedAbteilungen={selectedAbteilungen}
            onSelect={onSelect}
          />
        </Popover>
      </div>
    </>
  );
}
